import {WidgetErrorBanner} from 'shells/card/card-widget/widget-error-banner';
import {AddressRow} from 'shells/row/address-row/address-row';
import type {AddressValue} from 'shells/row/address-row/types';

import type {PeepBusinessCardInfoFragment_postalAddresses_all as PostalAddress} from 'nutshell-graphql-types';

import type {UserFacingEntity} from '../../../../core-entity/rows/peep-rows/types';

import {getTitleDropdownOptions} from '../helpers';
import type {PatchAddressInput, PatchTitleInput, RowErrorMessage} from '../types';

interface Props {
    addresses: PostalAddress[] | null | undefined;
    canMarkAsPrimary: boolean;
    parentEntity: UserFacingEntity;
    labelOptions: string[] | null | undefined;
    newlyAddedId?: string | null;
    onUpdateAddress: (args: PatchAddressInput) => Promise<any>;
    onUpdateTitle: (args: PatchTitleInput) => Promise<any>;
    rowErrorMessage: RowErrorMessage | null;
    setRowErrorMessage: (rowErrorMessage: RowErrorMessage | null) => void;
}

export function AddressRowList(props: Props) {
    return (
        <>
            {props.addresses?.map((postalAddress) => {
                const errorMessage =
                    props.rowErrorMessage?.id === postalAddress.id
                        ? props.rowErrorMessage.errorMessage
                        : null;

                const addressValue: AddressValue = {
                    address1: postalAddress.location?.address_1 ?? '',
                    address2: postalAddress.location?.address_2 ?? '',
                    address3: postalAddress.location?.address_3 ?? '',
                    city: postalAddress.location?.city ?? '',
                    state: postalAddress.location?.state ?? '',
                    postalCode: postalAddress.location?.postal_code ?? '',
                    country: postalAddress.location?.country ?? '',
                };
                const fullAddress = postalAddress.location?.fullPrintableAddress;

                if (!fullAddress) {
                    return null;
                }

                const dropdownOptions = getTitleDropdownOptions(
                    props.labelOptions,
                    postalAddress.id,
                    props.onUpdateTitle
                );

                return (
                    <div key={`${postalAddress.id}-postalAddress`}>
                        {errorMessage && (
                            <div className='pb-2 px-8'>
                                <WidgetErrorBanner
                                    error={errorMessage}
                                    setError={(error?: string | null) => {
                                        props.setRowErrorMessage(
                                            error
                                                ? {
                                                      id: postalAddress.id,
                                                      errorMessage: error,
                                                  }
                                                : null
                                        );
                                    }}
                                />
                            </div>
                        )}
                        <AddressRow
                            title={postalAddress.name}
                            showIsPrimaryIcon={props.addresses?.length !== 1}
                            parentEntity={props.parentEntity}
                            isPrimary={postalAddress.isPrimary}
                            isFormer={postalAddress.isFormer}
                            canMarkAsPrimary={props.canMarkAsPrimary}
                            fullAddress={fullAddress}
                            addressValue={addressValue}
                            dropdownOptions={dropdownOptions}
                            onUpdateAddress={(updatedAddress: AddressValue) =>
                                props.onUpdateAddress({
                                    attributeValueId: postalAddress.id,
                                    addressInput: {
                                        address_1: updatedAddress.address1,
                                        address_2: updatedAddress.address2,
                                        address_3: updatedAddress.address3,
                                        city: updatedAddress.city,
                                        state: updatedAddress.state,
                                        postalCode: updatedAddress.postalCode,
                                        country: updatedAddress.country,
                                    },
                                })
                            }
                            onMarkAsPrimary={() =>
                                props.onUpdateAddress({
                                    attributeValueId: postalAddress.id,
                                    isPrimary: true,
                                })
                            }
                            onChangeFormerStatus={(isFormer: boolean) =>
                                props.onUpdateAddress({
                                    attributeValueId: postalAddress.id,
                                    isFormer: isFormer,
                                })
                            }
                            onDelete={() =>
                                props.onUpdateAddress({
                                    attributeValueId: postalAddress.id,
                                    isDelete: true,
                                })
                            }
                            onUpdateTitle={(title: string) =>
                                props.onUpdateTitle({
                                    attributeValueId: postalAddress.id,
                                    name: title,
                                })
                            }
                            successFlashKey={
                                props.newlyAddedId === postalAddress.id
                                    ? postalAddress.id
                                    : undefined
                            }
                        />
                    </div>
                );
            })}
        </>
    );
}
